import { useId, useState } from 'react';
import { callbackStats, can, MIN_REASON, type OpsTenant } from '@urbanrats/ops-core';
import { ChangeRequests } from '../components/ChangeRequests.tsx';
import { PageHead, Section } from '../components/common.tsx';
import { fmtTime, useCtx } from '../lib.ts';
import { OperatorsAdmin } from './OperatorsAdmin.tsx';

export function Operators() {
  const { data, backend } = useCtx();
  // Backend: full onboarding (create / edit operators, sites, wallet, hosts). Demo: the read-only view.
  if (backend.kind === 'api' && backend.server) return <OperatorsAdmin />;
  const ops = [...new Set(data.tenants.map((t) => t.operatorId))];
  return (
    <>
      <PageHead title="Operators and brands">Keys are identified by id and fingerprint only; a secret is never displayed. Integration-ready (our conformance suite) and partner-certified (the partner’s acceptance) are separate statuses.</PageHead>
      {ops.map((op) => {
        const brands = data.tenants.filter((t) => t.operatorId === op);
        return (
          <Section key={op} title={`${brands[0]!.operatorName} · ${op}${brands[0]!.aggregatorId ? ` via ${brands[0]!.aggregatorId}` : ' (direct)'}`} testId={`operator-${op}`}>
            {brands.map((t) => (
              <Brand key={t.brandId} t={t} />
            ))}
          </Section>
        );
      })}
      <Section title="Key rotation requests">
        <ChangeRequests filter={(r) => r.kind === 'KEY_ROTATION'} />
      </Section>
    </>
  );
}

function Brand({ t }: { t: OpsTenant }) {
  const { data, user, backend, refresh } = useCtx();
  const [reason, setReason] = useState('');
  const [pem, setPem] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const id = useId();
  const pemId = useId();
  const api = backend.kind === 'api';
  const cb = callbackStats(data.callbacks.filter((c) => c.brandId === t.brandId));
  return (
    <div className="card" data-testid={`brand-${t.brandId}`}>
      <h3>
        {t.brandName} <span className="muted">· {t.brandId} · wallet currency {t.currency.code}</span>
      </h3>
      <dl className="kv">
        <dt>API key id</dt>
        <dd className="mono">{t.apiKey.keyId}</dd>
        <dt>Secret</dt>
        <dd data-testid="secret">•••••••• never displayed · fingerprint {t.apiKey.secretFingerprint}</dd>
        <dt>Last rotated</dt>
        <dd>{t.apiKey.lastRotatedAt ? fmtTime(t.apiKey.lastRotatedAt) : `never (created ${fmtTime(t.apiKey.createdAt)})`}</dd>
        <dt>Callback health</dt>
        <dd>
          {cb.ok}/{cb.attempts} OK · {cb.timeouts} timeouts · {cb.pending} pending · p95 {cb.p95LatencyMs ?? '—'} ms (SLO {t.callbackSloMs} ms){cb.simulated ? ' · simulated transport' : ''}
        </dd>
        <dt>Certification</dt>
        <dd>{t.certification}</dd>
      </dl>
      {can(user, 'request.keyRotation') && (
        <form
          className="form inline"
          onSubmit={async (e) => {
            e.preventDefault();
            const r = await backend.request(user, 'KEY_ROTATION', t.brandId, `Rotate API key of ${t.brandId}`, { operatorId: t.operatorId, brandId: t.brandId, ...(api ? { publicKeyPem: pem } : {}) }, reason);
            setMsg(r.ok ? `Request ${r.value.requestId} created — needs approval by a platform admin.` : `${r.error.code}: ${r.error.message}`);
            if (r.ok) {
              setReason('');
              setPem('');
            }
            refresh();
          }}
        >
          <label htmlFor={id}>Reason for key rotation (min {MIN_REASON} chars)</label>
          <input id={id} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="rotation-reason" />
          {api && (
            <>
              <label htmlFor={pemId}>New production public key (Ed25519, PEM) — generate the pair yourself; never send the private key</label>
              <textarea id={pemId} value={pem} onChange={(e) => setPem(e.target.value)} rows={4} data-testid="rotation-pem" />
            </>
          )}
          <button type="submit" className="btn" disabled={api && !pem.includes('BEGIN PUBLIC KEY')} data-testid="rotation-request">
            Request key rotation
          </button>
          {msg && (
            <p role="status" className="status-msg">
              {msg}
            </p>
          )}
        </form>
      )}
    </div>
  );
}
